import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Instagram,
  Facebook,
  Phone,
  Mail,
  Split,
  Ticket,
  RefreshCw,
  ScrollText,
  AlertCircle,
  Info,
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon,
  Package,
  TrendingUp,
  CheckCircle2,
  Archive,
  Tag,
} from "lucide-react";
import moment from "moment";
import { useState } from "react";
import { useOfferPermissions } from "../../hooks/permissionHooks/useOfferPermissions";
import PropertiesModal from "./PropertiesModal";
import VendorDealTitle from "./VendorDealTitle";
import { getVendorDealLabel } from "../../utils/productLabels";
import { Button } from "@mui/material";

/** API sends quantity 0 (or "0") when availability is unlimited. */
const isUnlimitedQuantity = (qty) => {
  if (qty === null || qty === undefined || qty === "") return true;
  if (typeof qty === "string" && qty.trim().toLowerCase() === "unlimited") {
    return true;
  }
  return Number(qty) === 0;
};

const ViewOffer = ({
  viewModal,
  onCloseViewModal,
  deal,
  handleEditNavigation,
}) => {
  const [activeTab, setActiveTab] = useState("details");
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showImageGallery, setShowImageGallery] = useState(false);
  const [isPropertiesModalOpen, setIsPropertiesModalOpen] = useState(false);
  const { canEditOffers } = useOfferPermissions();

  // Add helper function to check if deal is expired
  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    return moment(expiryDate).isBefore(moment());
  };

  // Helper function to check if flash sale is expired
  const isFlashSaleExpired = (flashData) => {
    if (!flashData) return false;
    // Handle both array and object formats
    const flashArray = Array.isArray(flashData) ? flashData : [flashData];
    return flashArray.some(
      (flash) =>
        flash &&
        flash.flash_end_time &&
        moment(flash.flash_end_time).isBefore(moment())
    );
  };

  // Helper function to check if flash sale is active
  const isFlashSaleActive = (flashData) => {
    if (!flashData) return false;
    // Handle both array and object formats
    const flashArray = Array.isArray(flashData) ? flashData : [flashData];
    return flashArray.some(
      (flash) =>
        flash &&
        flash.flash_start_time &&
        flash.flash_end_time &&
        moment().isBetween(
          moment(flash.flash_start_time),
          moment(flash.flash_end_time)
        )
    );
  };

  // Helper functions for image gallery
  const getAllImages = () => {
    const images = [];
    if (deal?.image) {
      images.push(deal.image);
    }
    if (deal?.images && deal.images.length > 0) {
      images.push(...deal.images);
    }
    return images;
  };

  const nextImage = () => {
    const images = getAllImages();
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    const images = getAllImages();
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const openImageGallery = (index = 0) => {
    setCurrentImageIndex(index);
    setShowImageGallery(true);
  };

  return (
    <AnimatePresence>
      {viewModal && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 z-[1000] backdrop-blur-sm "
            onClick={onCloseViewModal}
          />
          <div className="fixed inset-0 z-[1001] flex items-center justify-center p-4 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="pointer-events-auto w-full max-w-5xl bg-white rounded-2xl shadow-2xl max-h-[95vh] flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
            {/* Header */}
            <div className="flex flex-shrink-0 justify-between items-center p-4 border-b md:p-6">
              <h2 className="text-xl font-semibold text-gray-900 md:text-2xl">
                Deal Details
              </h2>
              <div className="flex gap-2 items-center md:gap-4">
                <div className="flex gap-1 items-center md:gap-2">
                  <span className="text-sm text-gray-600 md:text-base">
                    Status
                  </span>
                  <span
                    className={`px-3 py-1 text-sm font-medium text-white rounded-full ${
                      // Check flash sale first
                      deal?.validity_type === "flash_sale" &&
                      isFlashSaleExpired(deal.flash)
                        ? "bg-red-500"
                        : deal?.validity_type === "flash_sale" &&
                          isFlashSaleActive(deal.flash)
                        ? "bg-red-600 animate-pulse"
                        : deal?.validity_type === "flash_sale"
                        ? "bg-orange-500"
                        : // Then check regular expiry
                        isExpired(deal.expiry_date || deal.expiryDate)
                        ? "bg-red-500"
                        : deal.status === "Advertise"
                        ? "bg-purple-500"
                        : deal.status === "Pending"
                        ? "bg-orange-500"
                        : deal.status === "Approved"
                        ? "bg-green-500"
                        : "bg-red-500"
                    }`}
                  >
                    {(() => {
                      // Flash sale status
                      if (deal?.validity_type === "flash_sale") {
                        if (isFlashSaleExpired(deal.flash))
                          return "Flash Sale Expired";
                        if (isFlashSaleActive(deal.flash))
                          return "Flash Sale Active";
                        return "Flash Sale Scheduled";
                      }
                      // Regular status
                      if (isExpired(deal.expiry_date || deal.expiryDate))
                        return "Expired";
                      if (deal.status === "Advertise") return "Promoted";
                      return deal.status;
                    })()}
                  </span>
                </div>
                <button
                  onClick={onCloseViewModal}
                  className="p-2 rounded-xl transition-colors duration-200 hover:bg-gray-100"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="overflow-y-auto flex-grow p-4 md:p-6">
              <div className="grid grid-cols-1 gap-6 min-h-full md:grid-cols-2 md:gap-8">
                {/* Left Column - Image */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex flex-col overflow-hidden justify-center items-center p-6 bg-purple-100 rounded-2xl md:sticky md:top-0 min-h-[350px] object-fill"
                >
                  <div className="relative flex-1 w-full max-w-md">
                    <img
                      src={deal?.image || "https://example.com/placeholder.jpg"}
                      alt="Deal"
                      className="w-full h-full max-h-[280px] rounded-xl cursor-pointer hover:opacity-90 transition-opacity object-cover"
                      onClick={() => openImageGallery(0)}
                    />

                    {/* Image Gallery Indicator */}
                    {getAllImages().length > 1 && (
                      <div className="flex absolute top-2 right-2 gap-1 items-center px-2 py-1 text-xs text-white rounded-full bg-black/50">
                        <ImageIcon className="w-3 h-3" />
                        <span>{getAllImages().length}</span>
                      </div>
                    )}
                  </div>

                  {/* Additional Images Thumbnails */}
                  {deal?.images && deal.images.length > 0 && (
                    <div className="mt-4">
                      <div className="flex overflow-x-auto gap-3 pb-2 max-w-full">
                        {deal.images.slice(0, 5).map((image, index) => (
                          <img
                            key={index}
                            src={image}
                            alt={`Additional ${index + 1}`}
                            className="object-cover flex-shrink-0 w-20 h-20 rounded-lg border-2 border-gray-200 transition-opacity cursor-pointer hover:opacity-80 hover:border-purple-300"
                            onClick={() => openImageGallery(index + 1)}
                          />
                        ))}
                        {deal.images.length > 5 && (
                          <div
                            className="flex flex-shrink-0 justify-center items-center w-20 h-20 bg-gray-200 rounded-lg border-2 border-gray-200 transition-colors cursor-pointer hover:bg-gray-300 hover:border-purple-300"
                            onClick={() => openImageGallery(0)}
                          >
                            <span className="text-sm font-medium text-gray-600">
                              +{deal.images.length - 5}
                            </span>
                          </div>
                        )}
                      </div>
                      {deal.images.length > 1 && (
                        <p className="mt-2 text-xs text-center text-gray-500">
                          Click any image to view full gallery
                        </p>
                      )}
                    </div>
                  )}
                </motion.div>

                {/* Right Column - Details */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex flex-col space-y-4 md:space-y-6"
                >
                  <VendorDealTitle
                    deal={deal}
                    headlineClassName="text-lg font-bold text-gray-900 md:text-xl"
                    sublineClassName="mt-1 text-sm text-gray-500"
                    shortenProductName={false}
                  />

                  {/* Tabs Navigation */}
                  <div className="flex overflow-x-auto space-x-1 border-b scrollbar-hide">
                    {[
                      { id: "details", label: "Details" },
                      { id: "categories", label: "Categories" },
                      { id: "contact", label: "Contact" },
                      { id: "locations", label: "Locations" },
                      { id: "redemption", label: "Redemption" },
                      { id: "terms", label: "T&Cs" },
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`px-3 md:px-4 py-2 text-xs md:text-[12px] font-medium whitespace-nowrap transition-colors duration-200
                          ${
                            activeTab === tab.id
                              ? "text-pink-600 border-b-2 border-pink-600"
                              : "text-gray-600 hover:text-pink-600"
                          }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {/* Scrollable Tab Content */}
                  <div className="overflow-y-auto flex-1 md:max-h-[calc(95vh-320px)] pr-6 max-h-full">
                    <div className="pt-4 pr-2">
                      {activeTab === "details" && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="space-y-3"
                        >
                          {/* Price Information - Handle different offer types */}
                          {(() => {
                            // Helper function to check if BOGO has valid data
                            const hasBogoData = (bogo) => {
                              if (!bogo) return false;
                              // Handle both array and object formats
                              const bogoData = Array.isArray(bogo)
                                ? bogo[0]
                                : bogo;
                              return (
                                bogoData &&
                                (bogoData.bogo_item_price ||
                                  bogoData.item_price ||
                                  (bogoData.bogo_quantity &&
                                    bogoData.bogo_free_quantity) ||
                                  (bogoData.quantity && bogoData.free_quantity))
                              );
                            };

                            // Helper function to check if Bundle has valid data
                            const hasBundleData = (bundle) => {
                              if (!bundle) return false;
                              // Handle both array and object formats
                              const bundleData = Array.isArray(bundle)
                                ? bundle[0]
                                : bundle;
                              return (
                                bundleData &&
                                bundleData.original_sum &&
                                bundleData.bundle_price &&
                                bundleData.bundle_savings
                              );
                            };

                            // Determine offer type based on data structure
                            const offerType =
                              deal.offer_type ||
                              (hasBundleData(deal.bundle)
                                ? "bundle"
                                : hasBogoData(deal.bogo)
                                ? "bogo"
                                : deal.discounted_price &&
                                  deal.discounted_price !== 0
                                ? "discount"
                                : "standard");

                            // Only show pricing for discount offers or when no special offer type
                            if (
                              offerType === "discount" ||
                              (offerType === "standard" &&
                                deal.price &&
                                deal.price !== 0)
                            ) {
                              const originalPrice = deal.price;
                              const discountedPrice =
                                deal.discounted_price || deal.discountedPrice;
                              const discountPercentage =
                                deal.discount_percentage ||
                                deal.discountPercentage;

                              // Calculate discount percentage if not provided
                              const calculatedDiscount =
                                discountedPrice && originalPrice
                                  ? Math.round(
                                      ((originalPrice - discountedPrice) /
                                        originalPrice) *
                                        100
                                    )
                                  : discountPercentage;

                              return (
                                <div className="flex flex-col gap-2 sm:flex-row">
                                  <div className="flex-1 p-3 bg-gray-50 rounded-lg">
                                    <div className="flex flex-col">
                                      <span className="text-xs text-gray-500">
                                        Original Price
                                      </span>
                                      <div className="flex items-baseline">
                                        <span className="text-xl font-bold text-gray-900">
                                          ₵
                                          {parseFloat(originalPrice).toFixed(2)}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                  {discountedPrice && discountedPrice !== 0 && (
                                    <div className="flex-1 p-3 bg-gray-50 rounded-lg">
                                      <div className="flex flex-col">
                                        <span className="text-xs text-gray-500">
                                          Discounted Price
                                        </span>
                                        <div className="flex items-baseline">
                                          <span className="text-xl font-bold text-green-600">
                                            ₵
                                            {parseFloat(
                                              discountedPrice
                                            ).toFixed(2)}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                  {calculatedDiscount &&
                                    calculatedDiscount > 0 && (
                                      <div className="flex-1 p-3 bg-pink-50 rounded-lg">
                                        <div className="flex flex-col">
                                          <span className="text-xs text-pink-500">
                                            Discount
                                          </span>
                                          <span className="text-xl font-bold text-pink-600">
                                            {calculatedDiscount}%
                                          </span>
                                        </div>
                                      </div>
                                    )}
                                </div>
                              );
                            }
                            return null;
                          })()}

                          {/* Bundle Offer Information */}
                          {(() => {
                            // Helper function to check if Bundle has valid data
                            const hasBundleData = (bundle) => {
                              if (!bundle) return false;
                              // Handle both array and object formats
                              const bundleData = Array.isArray(bundle)
                                ? bundle[0]
                                : bundle;
                              return (
                                bundleData &&
                                bundleData.original_sum &&
                                bundleData.bundle_price &&
                                bundleData.bundle_savings
                              );
                            };

                            return hasBundleData(deal.bundle);
                          })() && (
                            <div className="p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-100">
                              <h4 className="mb-2 text-sm font-medium text-purple-700">
                                Bundle Offer Details
                              </h4>
                              {(() => {
                                // Handle both array and object formats
                                const bundleData = Array.isArray(deal.bundle)
                                  ? deal.bundle
                                  : [deal.bundle];
                                return bundleData.map((bundleItem, index) => (
                                  <div key={index} className="space-y-2">
                                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                                      <div className="p-2 bg-white rounded border">
                                        <span className="text-xs text-gray-500">
                                          Original Sum
                                        </span>
                                        <div className="text-lg font-bold text-gray-900">
                                          ₵
                                          {parseFloat(
                                            bundleItem.original_sum
                                          ).toFixed(2)}
                                        </div>
                                      </div>
                                      <div className="p-2 bg-white rounded border">
                                        <span className="text-xs text-gray-500">
                                          Bundle Price
                                        </span>
                                        <div className="text-lg font-bold text-green-600">
                                          ₵
                                          {parseFloat(
                                            bundleItem.bundle_price
                                          ).toFixed(2)}
                                        </div>
                                      </div>
                                      <div className="p-2 bg-white rounded border">
                                        <span className="text-xs text-gray-500">
                                          Total Savings
                                        </span>
                                        <div className="text-lg font-bold text-pink-600">
                                          ₵
                                          {parseFloat(
                                            bundleItem.bundle_savings
                                          ).toFixed(2)}
                                        </div>
                                      </div>
                                    </div>
                                    {bundleItem.bundle_skus &&
                                      bundleItem.bundle_skus.length > 0 && (
                                        <div className="mt-2">
                                          <span className="text-xs text-gray-500">
                                            Included Items:
                                          </span>
                                          <div className="flex flex-wrap gap-1 mt-1">
                                            {bundleItem.bundle_skus.map(
                                              (sku, skuIndex) => (
                                                <span
                                                  key={skuIndex}
                                                  className="px-2 py-1 text-xs text-purple-700 bg-purple-100 rounded"
                                                >
                                                  {sku.product_name} ({sku.sku})
                                                </span>
                                              )
                                            )}
                                          </div>
                                        </div>
                                      )}
                                  </div>
                                ));
                              })()}
                            </div>
                          )}

                          {/* BOGO Offer Information */}
                          {(() => {
                            // Helper function to check if BOGO has valid data
                            const hasBogoData = (bogo) => {
                              if (!bogo) return false;
                              // Handle both array and object formats
                              const bogoData = Array.isArray(bogo)
                                ? bogo[0]
                                : bogo;
                              return (
                                bogoData &&
                                (bogoData.bogo_item_price ||
                                  bogoData.item_price ||
                                  (bogoData.bogo_quantity &&
                                    bogoData.bogo_free_quantity) ||
                                  (bogoData.quantity && bogoData.free_quantity))
                              );
                            };

                            return hasBogoData(deal.bogo);
                          })() && (
                            <div className="p-3 bg-gradient-to-r from-blue-50 to-green-50 rounded-lg border border-blue-100">
                              <h4 className="mb-2 text-sm font-medium text-blue-700">
                                Buy One Get One Offer
                              </h4>
                              {(() => {
                                // Handle both array and object formats
                                const bogoData = Array.isArray(deal.bogo)
                                  ? deal.bogo
                                  : [deal.bogo];
                                return bogoData.map((bogoItem, index) => (
                                  <div key={index} className="space-y-2">
                                    {(bogoItem.bogo_item_price ||
                                      bogoItem.item_price) && (
                                      <div className="p-2 bg-white rounded border">
                                        <span className="text-xs text-gray-500">
                                          Item Price
                                        </span>
                                        <div className="text-lg font-bold text-blue-600">
                                          ₵
                                          {parseFloat(
                                            bogoItem.bogo_item_price ||
                                              bogoItem.item_price
                                          ).toFixed(2)}
                                        </div>
                                      </div>
                                    )}
                                    {(bogoItem.bogo_quantity &&
                                      bogoItem.bogo_free_quantity) ||
                                    (bogoItem.quantity &&
                                      bogoItem.free_quantity) ? (
                                      <div className="text-sm text-gray-600">
                                        Buy{" "}
                                        {bogoItem.bogo_quantity ||
                                          bogoItem.quantity}{" "}
                                        get{" "}
                                        {bogoItem.bogo_free_quantity ||
                                          bogoItem.free_quantity}{" "}
                                        free
                                      </div>
                                    ) : null}
                                  </div>
                                ));
                              })()}
                            </div>
                          )}

                          {/* Quantity Available Information */}
                          {(deal.quantity !== null &&
                            deal.quantity !== undefined) ||
                          deal.quantity === 0 ? (
                            <div className="p-4 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 rounded-xl border border-indigo-200 shadow-sm">
                              <div className="flex gap-3 items-center mb-4">
                                <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow-md">
                                  <Package className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                  <h4 className="text-sm font-semibold text-gray-900">
                                    Quantity Available
                                  </h4>
                                  <p className="text-xs text-gray-500">
                                    Items in stock for this deal
                                  </p>
                                </div>
                              </div>

                              {(() => {
                                const quantity = isUnlimitedQuantity(deal.quantity)
                                  ? null
                                  : Number(deal.quantity);

                                // Determine stock status
                                const getStockStatus = () => {
                                  if (quantity === null) {
                                    return {
                                      text: "Unlimited",
                                      color: "brand",
                                      icon: Package,
                                      bgColor: "from-[#FF6C6C]/10 to-pink-50",
                                      borderColor: "border-[#FF6C6C]/20",
                                      textColor: "text-[#FF6C6C]",
                                      iconBg: "bg-[#FF6C6C]",
                                    };
                                  }
                                  if (quantity < 10) {
                                    return {
                                      text: "Low stock",
                                      color: "orange",
                                      icon: AlertCircle,
                                      bgColor: "from-orange-50 to-orange-100",
                                      borderColor: "border-orange-200",
                                      textColor: "text-orange-700",
                                      iconBg: "bg-orange-500",
                                    };
                                  }
                                  if (quantity < 50) {
                                    return {
                                      text: "Moderate stock",
                                      color: "yellow",
                                      icon: Archive,
                                      bgColor: "from-yellow-50 to-yellow-100",
                                      borderColor: "border-yellow-200",
                                      textColor: "text-yellow-700",
                                      iconBg: "bg-yellow-500",
                                    };
                                  }
                                  if (quantity < 200) {
                                    return {
                                      text: "Good stock",
                                      color: "green",
                                      icon: CheckCircle2,
                                      bgColor: "from-green-50 to-green-100",
                                      borderColor: "border-green-200",
                                      textColor: "text-green-700",
                                      iconBg: "bg-green-500",
                                    };
                                  }
                                  return {
                                    text: "Excellent stock",
                                    color: "blue",
                                    icon: TrendingUp,
                                    bgColor: "from-blue-50 to-blue-100",
                                    borderColor: "border-blue-200",
                                    textColor: "text-blue-700",
                                    iconBg: "bg-blue-500",
                                  };
                                };

                                const stockStatus = getStockStatus();
                                const StatusIcon = stockStatus.icon;

                                return (
                                  <div
                                    className={`p-4 rounded-xl border-2 bg-gradient-to-br ${stockStatus.bgColor} ${stockStatus.borderColor}`}
                                  >
                                    <div className="flex justify-between items-center">
                                      <div className="flex gap-3 items-center">
                                        <motion.div
                                          className={`p-3 rounded-xl shadow-md ${stockStatus.iconBg}`}
                                          animate={{
                                            scale: [1, 1.05, 1],
                                          }}
                                          transition={{
                                            duration: 2,
                                            repeat: Infinity,
                                            repeatType: "reverse",
                                          }}
                                        >
                                          <StatusIcon className="w-6 h-6 text-white" />
                                        </motion.div>
                                        <div>
                                          <p className="mb-1 text-xs font-medium text-gray-600">
                                            Stock Status
                                          </p>
                                          <p
                                            className={`text-lg font-bold ${stockStatus.textColor}`}
                                          >
                                            {stockStatus.text}
                                          </p>
                                        </div>
                                      </div>

                                      <div className="text-right">
                                        <p className="mb-1 text-xs font-medium text-gray-600">
                                          Total Items
                                        </p>
                                        <motion.p
                                          className={`text-3xl font-bold ${stockStatus.textColor}`}
                                          initial={{ scale: 1.2, opacity: 0 }}
                                          animate={{ scale: 1, opacity: 1 }}
                                          transition={{
                                            type: "spring",
                                            stiffness: 300,
                                          }}
                                        >
                                          {quantity === null
                                            ? "∞"
                                            : quantity.toLocaleString()}
                                        </motion.p>
                                        {quantity === null && (
                                          <p className="mt-1 text-xs text-gray-500">
                                            Unlimited
                                          </p>
                                        )}
                                      </div>
                                    </div>

                                    {/* Stock Level Progress Bar */}
                                    {quantity !== null && quantity > 0 && (
                                      <div className="mt-4">
                                        <div className="flex justify-between items-center mb-2">
                                          <span className="text-xs font-medium text-gray-600">
                                            Stock Level
                                          </span>
                                          <span className="text-xs font-semibold text-gray-700">
                                            {Math.min(
                                              100,
                                              Math.round(
                                                (quantity / 1000) * 100
                                              )
                                            )}
                                            %
                                          </span>
                                        </div>
                                        <div className="w-full h-2.5 bg-white/50 rounded-full overflow-hidden shadow-inner">
                                          <motion.div
                                            className={`h-full rounded-full shadow-sm bg-gradient-to-r ${
                                              stockStatus.color === "red"
                                                ? "from-red-500 to-red-600"
                                                : stockStatus.color === "orange"
                                                ? "from-orange-500 to-orange-600"
                                                : stockStatus.color === "yellow"
                                                ? "from-yellow-500 to-yellow-600"
                                                : stockStatus.color === "green"
                                                ? "from-green-500 to-green-600"
                                                : stockStatus.color === "brand"
                                                ? "from-[#FF6C6C] to-[#FF8F8F]"
                                                : "from-blue-500 to-blue-600"
                                            }`}
                                            initial={{ width: 0 }}
                                            animate={{
                                              width: `${Math.min(
                                                100,
                                                Math.round(
                                                  (quantity / 1000) * 100
                                                )
                                              )}%`,
                                            }}
                                            transition={{
                                              duration: 0.5,
                                              ease: "easeOut",
                                            }}
                                          />
                                        </div>
                                      </div>
                                    )}

                                    {/* Helpful Message */}
                                    <motion.div
                                      initial={{ opacity: 0 }}
                                      animate={{ opacity: 1 }}
                                      transition={{ delay: 0.2 }}
                                      className="flex gap-2 items-start p-3 mt-4 rounded-xl bg-white/60"
                                    >
                                      <Package className="w-4 h-4 text-gray-600 flex-shrink-0 mt-0.5" />
                                      <p className="text-xs leading-relaxed text-gray-700">
                                        {quantity === null
                                          ? "Unlimited availability is set. Quantity tracking is disabled for this deal."
                                          : quantity < 10
                                          ? "Low stock alert! Limited items available. Act fast before they run out."
                                          : quantity < 50
                                          ? "Moderate stock available. Good selection still in store."
                                          : "Excellent stock levels! Plenty of items available for customers."}
                                      </p>
                                    </motion.div>
                                  </div>
                                );
                              })()}
                            </div>
                          ) : null}

                          {/* Flash Sale Information */}
                          {deal.validity_type === "flash_sale" &&
                            deal.flash &&
                            (() => {
                              // Handle both array and object formats
                              const flashData = Array.isArray(deal.flash)
                                ? deal.flash
                                : [deal.flash];
                              return flashData.some(
                                (flash) =>
                                  flash.flash_start_time && flash.flash_end_time
                              );
                            })() && (
                              <div className="p-3 bg-gradient-to-r from-red-50 to-orange-50 rounded-lg border border-red-100">
                                <h4 className="mb-2 text-sm font-medium text-red-700">
                                  Flash Sale Details
                                </h4>
                                {(() => {
                                  // Handle both array and object formats
                                  const flashData = Array.isArray(deal.flash)
                                    ? deal.flash
                                    : [deal.flash];
                                  return flashData.map((flashItem, index) => (
                                    <div key={index} className="space-y-2">
                                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                        <div className="p-2 bg-white rounded border">
                                          <span className="text-xs text-gray-500">
                                            Start Time
                                          </span>
                                          <div className="text-sm font-bold text-red-600">
                                            {moment(
                                              flashItem.flash_start_time
                                            ).format("DD MMM, YYYY HH:mm")}
                                          </div>
                                        </div>
                                        <div className="p-2 bg-white rounded border">
                                          <span className="text-xs text-gray-500">
                                            End Time
                                          </span>
                                          <div className="text-sm font-bold text-red-600">
                                            {moment(
                                              flashItem.flash_end_time
                                            ).format("DD MMM, YYYY HH:mm")}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="p-2 bg-white rounded border">
                                        <span className="text-xs text-gray-500">
                                          Duration
                                        </span>
                                        <div className="text-sm font-bold text-orange-600">
                                          {flashItem.flash_duration} hours
                                        </div>
                                      </div>
                                    </div>
                                  ));
                                })()}
                              </div>
                            )}

                          {/* Properties - Button to open modal */}
                          {deal.properties && deal.properties.length > 0 && (
                            <motion.div
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              className="p-4 rounded-xl border sm:p-5"
                              style={{
                                background:
                                  "linear-gradient(to right, rgba(254, 105, 111, 0.1), rgba(254, 105, 111, 0.05))",
                                borderColor: "rgba(254, 105, 111, 0.2)",
                              }}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div
                                    className="p-2.5 rounded-lg"
                                    style={{
                                      backgroundColor:
                                        "rgba(254, 105, 111, 0.15)",
                                    }}
                                  >
                                    <Tag
                                      className="w-5 h-5"
                                      style={{ color: "#FE696F" }}
                                    />
                                  </div>
                                  <div>
                                    <h4
                                      className="font-semibold"
                                      style={{ color: "#FE696F" }}
                                    >
                                      Deal Properties
                                    </h4>
                                    <p
                                      className="text-sm mt-0.5"
                                      style={{ color: "#FE696F" }}
                                    >
                                      {deal.properties.length}{" "}
                                      {deal.properties.length === 1
                                        ? "property"
                                        : "properties"}{" "}
                                      available
                                    </p>
                                  </div>
                                </div>
                                <Button
                                  variant="contained"
                                  onClick={() => setIsPropertiesModalOpen(true)}
                                  startIcon={<Tag className="w-4 h-4" />}
                                  sx={{
                                    backgroundColor: "#FE696F",
                                    borderRadius: "12px",
                                    padding: "10px 20px",
                                    textTransform: "none",
                                    fontWeight: 600,
                                    boxShadow:
                                      "0 4px 6px -1px rgba(254, 105, 111, 0.3)",
                                    "&:hover": {
                                      backgroundColor: "#ff5555",
                                      boxShadow:
                                        "0 6px 8px -1px rgba(254, 105, 111, 0.4)",
                                    },
                                  }}
                                >
                                  View Properties
                                </Button>
                              </div>
                            </motion.div>
                          )}

                          {/* Exclusions */}
                          {deal.exclusions && deal.exclusions.length > 0 && (
                            <div className="p-3 bg-red-50 rounded-lg border border-red-100">
                              <h4 className="mb-2 text-sm font-medium text-red-700">
                                Exclusions
                              </h4>
                              <ul className="space-y-1">
                                {deal.exclusions.map((exclusion, index) => (
                                  <li
                                    key={index}
                                    className="flex gap-2 items-start text-sm text-red-600"
                                  >
                                    <span className="text-red-500 mt-0.5">
                                      •
                                    </span>
                                    <span>{exclusion}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Offer Description */}
                          <div className="p-3 bg-gray-50 rounded-lg">
                            <h4 className="mb-1 text-xs font-medium text-gray-700">
                              Offer Description
                            </h4>
                            <p className="text-sm text-gray-600">
                              {deal?.details?.description ||
                                "Get amazing discounts on all electronic items. Valid on selected products only. Visit our store or website for more details."}
                            </p>
                          </div>

                          {/* Age Restrictions & Redemption Limits */}
                          {(() => {
                            const hasAgeRestrictions =
                              deal.age &&
                              (deal.age.min_age ||
                                deal.age.max_age ||
                                deal.age.no_age_limit);

                            const hasRedemptionLimits =
                              deal.redemption_limits &&
                              Array.isArray(deal.redemption_limits) &&
                              deal.redemption_limits.length > 0;

                            if (!hasAgeRestrictions && !hasRedemptionLimits) {
                              return null;
                            }

                            return (
                              <div className="p-4 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 rounded-xl border border-indigo-200 shadow-sm">
                                <div className="flex gap-2 items-center mb-4">
                                  <div className="w-2 h-2 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full"></div>
                                  <h4 className="text-sm font-semibold text-indigo-800">
                                    Restrictions & Limits
                                  </h4>
                                </div>

                                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                  {/* Age Restrictions */}
                                  {hasAgeRestrictions && (
                                    <div className="p-3 rounded-lg border border-indigo-100 backdrop-blur-sm transition-all duration-200 bg-white/70 hover:border-indigo-200">
                                      <div className="flex gap-2 items-center mb-2">
                                        <svg
                                          className="w-4 h-4 text-indigo-600"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                                          />
                                        </svg>
                                        <span className="text-xs font-medium text-indigo-700">
                                          Age Restrictions
                                        </span>
                                      </div>

                                      {deal.age.no_age_limit ? (
                                        <div className="flex gap-2 items-center">
                                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                          <span className="text-sm font-medium text-green-700">
                                            No age restrictions
                                          </span>
                                        </div>
                                      ) : (
                                        <div className="space-y-1">
                                          {deal.age.min_age && (
                                            <div className="flex gap-2 items-center">
                                              <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                                              <span className="text-sm text-gray-700">
                                                Minimum age:{" "}
                                                <span className="font-semibold text-indigo-700">
                                                  {deal.age.min_age} years
                                                </span>
                                              </span>
                                            </div>
                                          )}
                                          {deal.age.max_age && (
                                            <div className="flex gap-2 items-center">
                                              <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                                              <span className="text-sm text-gray-700">
                                                Maximum age:{" "}
                                                <span className="font-semibold text-purple-700">
                                                  {deal.age.max_age} years
                                                </span>
                                              </span>
                                            </div>
                                          )}
                                          {deal.age.min_age &&
                                            deal.age.max_age && (
                                              <div className="p-2 mt-2 bg-gradient-to-r from-indigo-100 to-purple-100 rounded-md">
                                                <span className="text-xs font-medium text-indigo-800">
                                                  Age range: {deal.age.min_age}{" "}
                                                  - {deal.age.max_age} years
                                                </span>
                                              </div>
                                            )}
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {/* Redemption Limits */}
                                  {hasRedemptionLimits && (
                                    <div className="p-3 rounded-lg border border-purple-100 backdrop-blur-sm transition-all duration-200 bg-white/70 hover:border-purple-200">
                                      <div className="flex gap-2 items-center mb-2">
                                        <svg
                                          className="w-4 h-4 text-purple-600"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                          />
                                        </svg>
                                        <span className="text-xs font-medium text-purple-700">
                                          Redemption Limits
                                        </span>
                                      </div>

                                      {deal.redemption_limits.length === 0 ? (
                                        <div className="flex gap-2 items-center">
                                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                          <span className="text-sm font-medium text-green-700">
                                            No redemption limits
                                          </span>
                                        </div>
                                      ) : (
                                        <div className="space-y-2">
                                          {deal.redemption_limits.map(
                                            (limit, index) => (
                                              <div
                                                key={index}
                                                className="flex gap-2 items-center"
                                              >
                                                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                                                <span className="text-sm text-gray-700">
                                                  <span className="font-semibold text-purple-700">
                                                    {limit}
                                                  </span>
                                                </span>
                                              </div>
                                            )
                                          )}
                                          <div className="p-2 mt-2 bg-gradient-to-r from-purple-100 to-pink-100 rounded-md">
                                            <span className="text-xs font-medium text-purple-800">
                                              {deal.redemption_limits.length}{" "}
                                              {deal.redemption_limits.length ===
                                              1
                                                ? "limit"
                                                : "limits"}{" "}
                                              applied
                                            </span>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>

                                {/* Summary Badge */}
                                <div className="flex justify-center mt-3">
                                  <div className="px-3 py-1 bg-gradient-to-r from-indigo-100 to-purple-100 rounded-full">
                                    <span className="text-xs font-medium text-indigo-800">
                                      {hasAgeRestrictions && hasRedemptionLimits
                                        ? "Age & Redemption restrictions apply"
                                        : hasAgeRestrictions
                                        ? "Age restrictions apply"
                                        : "Redemption limits apply"}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })()}

                          {/* Validity Information */}
                          {deal?.validity_type === "flash_sale" &&
                          deal?.flash &&
                          (() => {
                            // Handle both array and object formats
                            const flashData = Array.isArray(deal.flash)
                              ? deal.flash
                              : [deal.flash];
                            return flashData.some(
                              (flash) =>
                                flash.flash_start_time && flash.flash_end_time
                            );
                          })() ? (
                            // Flash Sale Validity
                            <div className="p-3 bg-gradient-to-r from-red-50 to-orange-50 rounded-lg border border-red-100">
                              <div className="flex justify-between items-center mb-2">
                                <div className="flex gap-2 items-center">
                                  <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                                  <span className="text-sm font-medium text-red-700">
                                    Flash Sale
                                  </span>
                                </div>
                                <span className="px-2 py-1 text-xs font-medium text-red-700 bg-red-100 rounded-full">
                                  Limited Time
                                </span>
                              </div>
                              {(() => {
                                // Handle both array and object formats
                                const flashData = Array.isArray(deal.flash)
                                  ? deal.flash
                                  : [deal.flash];
                                return flashData.map((flashItem, index) => {
                                  if (
                                    !flashItem ||
                                    !flashItem.flash_start_time ||
                                    !flashItem.flash_end_time
                                  ) {
                                    return null;
                                  }

                                  const isFlashExpired = moment(
                                    flashItem.flash_end_time
                                  ).isBefore(moment());
                                  const isFlashActive = moment().isBetween(
                                    moment(flashItem.flash_start_time),
                                    moment(flashItem.flash_end_time)
                                  );

                                  return (
                                    <div key={index} className="space-y-2">
                                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                        <div className="p-2 bg-white rounded border">
                                          <span className="text-xs text-gray-500">
                                            Start Time
                                          </span>
                                          <div className="text-sm font-bold text-red-600">
                                            {moment(
                                              flashItem.flash_start_time
                                            ).format("DD MMM, YYYY HH:mm")}
                                          </div>
                                        </div>
                                        <div className="p-2 bg-white rounded border">
                                          <span className="text-xs text-gray-500">
                                            End Time
                                          </span>
                                          <div className="text-sm font-bold text-red-600">
                                            {moment(
                                              flashItem.flash_end_time
                                            ).format("DD MMM, YYYY HH:mm")}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="flex justify-between items-center">
                                        <div className="text-sm text-gray-600">
                                          Duration:{" "}
                                          <span className="font-medium">
                                            {flashItem.flash_duration || "N/A"}{" "}
                                            hours
                                          </span>
                                        </div>
                                        {!isFlashExpired && (
                                          <div className="px-3 py-1 bg-red-100 rounded-md">
                                            <span className="text-xs font-medium text-red-600">
                                              {isFlashActive ? (
                                                <>
                                                  {moment(
                                                    flashItem.flash_end_time
                                                  ).diff(moment(), "hours")}
                                                  h{" "}
                                                  {moment(
                                                    flashItem.flash_end_time
                                                  ).diff(moment(), "minutes") %
                                                    60}
                                                  m left
                                                </>
                                              ) : (
                                                <>
                                                  Starts in{" "}
                                                  {moment(
                                                    flashItem.flash_start_time
                                                  ).diff(moment(), "hours")}
                                                  h{" "}
                                                  {moment(
                                                    flashItem.flash_start_time
                                                  ).diff(moment(), "minutes") %
                                                    60}
                                                  m
                                                </>
                                              )}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                });
                              })()}
                            </div>
                          ) : deal?.expiry_date || deal?.expiryDate ? (
                            // Regular/Standard Validity
                            <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                              <div className="flex justify-between items-center">
                                <div className="flex gap-2 items-center">
                                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                  <div className="flex flex-col">
                                    <span className="text-xs text-blue-500">
                                      {isExpired(
                                        deal.expiry_date || deal.expiryDate
                                      )
                                        ? "Expired On"
                                        : "Valid Until"}
                                    </span>
                                    <span className="text-sm font-semibold text-blue-600">
                                      {moment(
                                        deal?.expiry_date || deal?.expiryDate
                                      ).format("DD MMM, YYYY")}
                                    </span>
                                  </div>
                                </div>
                                {!isExpired(
                                  deal.expiry_date || deal.expiryDate
                                ) && (
                                  <div className="px-3 py-1 bg-blue-100 rounded-md">
                                    <span className="text-xs font-medium text-blue-600">
                                      {moment(
                                        deal?.expiry_date || deal?.expiryDate
                                      ).diff(moment(), "days")}{" "}
                                      {moment(
                                        deal?.expiry_date || deal?.expiryDate
                                      ).diff(moment(), "days") === 1
                                        ? "day left"
                                        : "days left"}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : null}
                        </motion.div>
                      )}

                      {activeTab === "categories" && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="space-y-6"
                        >
                          <div className="p-6 bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-100 shadow-sm">
                            <div className="flex gap-3 items-center mb-6">
                              <div className="w-1 h-8 bg-gradient-to-b from-pink-500 to-purple-600 rounded-full" />
                              <h4 className="text-lg font-semibold text-gray-900">
                                Categories
                              </h4>
                            </div>

                            <div className="space-y-5">
                              <div className="flex flex-wrap gap-2 md:gap-3">
                                {(deal?.categories?.length
                                  ? deal.categories.map((item) => item?.name).filter(Boolean)
                                  : deal?.category || []
                                ).map((category, index) => (
                                  <motion.span
                                    key={`category-${category}-${index}`}
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: index * 0.05 }}
                                    className="relative group"
                                  >
                                    <div className="absolute inset-0 bg-gradient-to-r from-pink-500 to-purple-600 rounded-xl opacity-0 blur-lg transition-opacity duration-300 group-hover:opacity-100" />
                                    <div
                                      className="relative px-4 py-2.5 bg-white border border-gray-200 
                                                text-gray-700 rounded-xl text-sm font-medium
                                                group-hover:border-transparent group-hover:text-white
                                                group-hover:bg-gradient-to-r from-pink-500 to-purple-600 
                                                transition-all duration-300 shadow-sm
                                                hover:shadow-lg hover:shadow-pink-500/10"
                                    >
                                      {category}
                                    </div>
                                  </motion.span>
                                ))}
                              </div>
                              {(() => {
                                const subcategories = deal?.subcategories?.length
                                  ? deal.subcategories.map((item) => item?.name).filter(Boolean)
                                  : deal?.product_subcategory
                                    ? [deal.product_subcategory]
                                    : [];
                                if (!subcategories.length) return null;
                                return (
                                  <div>
                                    <p className="mb-3 text-xs font-semibold tracking-wide text-gray-500">Subcategories</p>
                                    <div className="flex flex-wrap gap-2 md:gap-3">
                                      {subcategories.map((subcategory, index) => (
                                        <motion.span
                                          key={`subcategory-${subcategory}-${index}`}
                                          initial={{ opacity: 0, scale: 0.95 }}
                                          animate={{ opacity: 1, scale: 1 }}
                                          transition={{ delay: index * 0.05 }}
                                          className="px-4 py-2.5 bg-gray-50 border border-gray-200 text-gray-700 rounded-xl text-sm font-medium"
                                        >
                                          {subcategory}
                                        </motion.span>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>

                            <div className="flex gap-2 items-center mt-6 text-sm text-gray-500">
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                              </svg>
                              <span>
                                Category and subcategory for this deal
                              </span>
                            </div>
                          </div>
                        </motion.div>
                      )}

                      {activeTab === "contact" && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="space-y-4"
                        >
                          {/* Social Media Section */}
                          <div>
                            <h4 className="mb-2 text-sm font-medium text-gray-700">
                              Social Media
                            </h4>
                            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                              {(deal?.contact?.social_media?.instagram ||
                                deal?.contact?.socialMedia?.instagram) && (
                                <motion.a
                                  whileHover={{ scale: 1.02 }}
                                  whileTap={{ scale: 0.98 }}
                                  href={
                                    deal?.contact?.social_media?.instagram ||
                                    deal?.contact?.socialMedia?.instagram
                                  }
                                  className="flex gap-2 justify-center items-center px-4 py-3 text-white bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg shadow-md transition-shadow duration-200 shadow-pink-500/10 hover:shadow-pink-500/20"
                                >
                                  <Instagram className="w-5 h-5" />
                                  <span className="text-sm font-medium">
                                    Instagram
                                  </span>
                                </motion.a>
                              )}

                              {(deal?.contact?.social_media?.facebook ||
                                deal?.contact?.socialMedia?.facebook) && (
                                <motion.a
                                  whileHover={{ scale: 1.02 }}
                                  whileTap={{ scale: 0.98 }}
                                  href={
                                    deal?.contact?.social_media?.facebook ||
                                    deal?.contact?.socialMedia?.facebook
                                  }
                                  className="flex gap-2 justify-center items-center px-4 py-3 text-white bg-gradient-to-r from-blue-600 to-blue-500 rounded-lg shadow-md transition-shadow duration-200 shadow-blue-500/10 hover:shadow-blue-500/20"
                                >
                                  <Facebook className="w-5 h-5" />
                                  <span className="text-sm font-medium">
                                    Facebook
                                  </span>
                                </motion.a>
                              )}
                              {(deal?.contact?.social_media?.twitter ||
                                deal?.contact?.socialMedia?.twitter) && (
                                <motion.a
                                  whileHover={{ scale: 1.02 }}
                                  whileTap={{ scale: 0.98 }}
                                  href={
                                    deal?.contact?.social_media?.twitter ||
                                    deal?.contact?.socialMedia?.twitter
                                  }
                                  className="flex gap-2 justify-center items-center px-4 py-3 text-white bg-black rounded-lg shadow-md transition-all duration-200 hover:bg-neutral-800"
                                >
                                  <svg
                                    viewBox="0 0 24 24"
                                    className="w-5 h-5 fill-current"
                                    aria-hidden="true"
                                  >
                                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                                  </svg>
                                  <span className="text-sm font-medium">
                                    Twitter
                                  </span>
                                </motion.a>
                              )}
                            </div>
                          </div>

                          {/* Contact Information */}
                          <div>
                            <h4 className="mb-2 text-sm font-medium text-gray-700">
                              Contact Details
                            </h4>
                            <div className="grid grid-cols-1 gap-2">
                              {/* Phone Number */}
                              {(deal?.contact?.phone_number ||
                                deal?.contact?.phoneNumber ||
                                deal?.phone_number) && (
                                <motion.div
                                  initial={{ opacity: 0, x: -10 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: 0.1 }}
                                  className="flex gap-3 items-center p-3 bg-white rounded-lg border border-gray-100 transition-colors duration-200 hover:border-pink-200 hover:bg-pink-50"
                                >
                                  <div className="p-2 bg-pink-100 rounded-md">
                                    <Phone className="w-5 h-5 text-pink-600" />
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-500 mb-0.5">
                                      Phone Number
                                    </p>
                                    <a
                                      href={`tel:${
                                        deal?.contact?.phone_number ||
                                        deal?.contact?.phoneNumber ||
                                        deal?.phone_number
                                      }`}
                                      className="text-sm font-medium text-gray-900 hover:text-pink-600"
                                    >
                                      {deal?.contact?.phone_number ||
                                        deal?.contact?.phoneNumber ||
                                        deal?.phone_number}
                                    </a>
                                  </div>
                                </motion.div>
                              )}
                              {/* Email */}
                              {deal?.contact?.email && (
                                <motion.div
                                  initial={{ opacity: 0, x: -10 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: 0.1 }}
                                  className="flex gap-3 items-center p-3 bg-white rounded-lg border border-gray-100 transition-colors duration-200 hover:border-blue-200 hover:bg-blue-50"
                                >
                                  <div className="p-2 bg-blue-100 rounded-md">
                                    <Mail className="w-5 h-5 text-blue-600" />
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-500 mb-0.5">
                                      Email
                                    </p>
                                    <a
                                      href={`mailto:${deal?.contact?.email}`}
                                      className="text-sm font-medium text-gray-900 hover:text-blue-600"
                                    >
                                      {deal?.contact?.email}
                                    </a>
                                  </div>
                                </motion.div>
                              )}
                              {/* Website */}
                              {(deal?.contact?.website || deal?.online_link) &&
                                deal?.redemption_type &&
                                Array.isArray(deal.redemption_type) &&
                                deal.redemption_type.includes("Online") && (
                                  <motion.div
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.2 }}
                                    className="flex gap-3 items-center p-3 bg-white rounded-lg border border-gray-100 transition-colors duration-200 hover:border-purple-200 hover:bg-purple-50"
                                  >
                                    <div className="p-2.5 bg-purple-100 rounded-md">
                                      <svg
                                        className="w-5 h-5 text-purple-600"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                        />
                                      </svg>
                                    </div>
                                    <div>
                                      <p className="mb-1 text-sm text-gray-500">
                                        Website
                                      </p>
                                      <a
                                        href={
                                          deal?.contact?.website ||
                                          deal?.online_link
                                        }
                                        className="text-base font-medium text-gray-900 hover:text-purple-600"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        {deal?.contact?.website ||
                                          deal?.online_link}
                                      </a>
                                    </div>
                                  </motion.div>
                                )}
                            </div>
                          </div>

                          {/* Support Hours Notice */}
                          <div className="flex gap-2 items-center px-3 py-2 text-blue-600 bg-blue-50 rounded-lg">
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                            <span className="text-xs">
                              Support available Monday to Friday, 9AM - 5PM
                            </span>
                          </div>
                        </motion.div>
                      )}

                      {activeTab === "locations" && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="space-y-6"
                        >
                          {/* Target Locations Section */}
                          <div>
                            <div className="flex gap-3 items-center mb-4">
                              <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-blue-600 rounded-full" />
                              <h4 className="text-lg font-semibold text-gray-900">
                                Target Locations
                              </h4>
                            </div>

                            {(() => {
                              const locationSections = [];

                              // Handle target_branches (array of branch objects)
                              if (
                                deal.target_branches &&
                                Array.isArray(deal.target_branches) &&
                                deal.target_branches.length > 0
                              ) {
                                locationSections.push(
                                  <div
                                    key="target_branches"
                                    className="space-y-3"
                                  >
                                    <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 shadow-sm">
                                      <div className="flex gap-2 items-center mb-3">
                                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                        <h5 className="font-semibold text-blue-800">
                                          Target Branches
                                        </h5>
                                        <span className="px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded-full">
                                          {deal.target_branches.length}{" "}
                                          {deal.target_branches.length === 1
                                            ? "Branch"
                                            : "Branches"}
                                        </span>
                                      </div>
                                      <div className="grid grid-cols-1 gap-3">
                                        {deal.target_branches.map(
                                          (branch, index) => (
                                            <div
                                              key={index}
                                              className="p-3 bg-white rounded-lg border border-blue-100 transition-all duration-200 hover:border-blue-200 hover:shadow-sm"
                                            >
                                              <div className="flex gap-3 items-start">
                                                <div className="flex-shrink-0 mt-2 w-2 h-2 bg-blue-500 rounded-full" />
                                                <div className="flex-1 min-w-0">
                                                  <h6 className="mb-1 font-medium text-gray-900">
                                                    {branch.name ||
                                                      branch.branch_name}
                                                  </h6>
                                                  <div className="flex gap-2 items-center text-sm text-gray-600">
                                                    <Split className="flex-shrink-0 w-4 h-4 text-blue-500" />
                                                    <span>
                                                      {branch.location},{" "}
                                                      {branch.region}
                                                    </span>
                                                  </div>
                                                  {branch.phone_number && (
                                                    <div className="flex gap-2 items-center mt-1 text-sm text-gray-600">
                                                      <Phone className="flex-shrink-0 w-4 h-4 text-blue-500" />
                                                      <a
                                                        href={`tel:${branch.phone_number}`}
                                                        className="hover:text-blue-600"
                                                      >
                                                        {branch.phone_number}
                                                      </a>
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          )
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              }

                              // Handle target_partners (array of partner objects with branches)
                              if (
                                deal.target_partners &&
                                Array.isArray(deal.target_partners) &&
                                deal.target_partners.length > 0
                              ) {
                                locationSections.push(
                                  <div
                                    key="target_partners"
                                    className="space-y-4"
                                  >
                                    {deal.target_partners.map(
                                      (partner, partnerIndex) => (
                                        <div
                                          key={partnerIndex}
                                          className="overflow-hidden bg-white rounded-xl border border-blue-100 shadow-sm"
                                        >
                                          <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100">
                                            <div className="flex justify-between items-center">
                                              <div className="flex gap-2 items-center">
                                                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                                <h5 className="font-semibold text-blue-800">
                                                  {partner.partner_name}
                                                </h5>
                                              </div>
                                              <span className="px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded-full">
                                                {partner.branches?.length || 0}{" "}
                                                {partner.branches?.length === 1
                                                  ? "Branch"
                                                  : "Branches"}
                                              </span>
                                            </div>
                                          </div>

                                          {partner.branches &&
                                            partner.branches.length > 0 && (
                                              <div className="p-4">
                                                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                                  {partner.branches.map(
                                                    (branch, branchIndex) => (
                                                      <div
                                                        key={branchIndex}
                                                        className="p-3 bg-white rounded-lg border border-gray-100 transition-colors duration-200 hover:border-blue-200 hover:bg-blue-50/30"
                                                      >
                                                        <div className="flex gap-2 items-start mb-2">
                                                          <div className="w-2 h-2 bg-blue-500 rounded-full mt-1.5 flex-shrink-0" />
                                                          <div className="flex-1 min-w-0">
                                                            <h6 className="font-medium text-gray-900 truncate">
                                                              {branch.name}
                                                            </h6>
                                                          </div>
                                                        </div>

                                                        <div className="space-y-1">
                                                          <div className="flex gap-2 items-center text-sm text-gray-600">
                                                            <Split className="flex-shrink-0 w-4 h-4 text-blue-500" />
                                                            <span className="truncate">
                                                              {branch.region} -{" "}
                                                              {branch.location}
                                                            </span>
                                                          </div>

                                                          {branch.phone_number && (
                                                            <div className="flex gap-2 items-center text-sm text-gray-600">
                                                              <Phone className="flex-shrink-0 w-4 h-4 text-blue-500" />
                                                              <a
                                                                href={`tel:${branch.phone_number}`}
                                                                className="truncate hover:text-blue-600"
                                                              >
                                                                {
                                                                  branch.phone_number
                                                                }
                                                              </a>
                                                            </div>
                                                          )}
                                                        </div>
                                                      </div>
                                                    )
                                                  )}
                                                </div>
                                              </div>
                                            )}
                                        </div>
                                      )
                                    )}
                                  </div>
                                );
                              }

                              // Handle partner_location (array of partner objects with branches)
                              console.log(
                                "Partner location data:",
                                deal.partner_location
                              );

                              // Parse partner_location if it's a JSON string
                              let partnerLocationData = deal.partner_location;
                              if (typeof deal.partner_location === "string") {
                                try {
                                  partnerLocationData = JSON.parse(
                                    deal.partner_location
                                  );
                                } catch (error) {
                                  partnerLocationData = null;
                                }
                              }

                              if (
                                partnerLocationData &&
                                Array.isArray(partnerLocationData) &&
                                partnerLocationData.length > 0
                              ) {
                                locationSections.push(
                                  <div
                                    key="partner_location"
                                    className="space-y-4"
                                  >
                                    {partnerLocationData.map(
                                      (partner, partnerIndex) => (
                                        <div
                                          key={partnerIndex}
                                          className="overflow-hidden bg-white rounded-xl border border-purple-100 shadow-sm"
                                        >
                                          <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 border-b border-purple-100">
                                            <div className="flex justify-between items-center">
                                              <div className="flex gap-2 items-center">
                                                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                                                <h5 className="font-semibold text-purple-800">
                                                  {partner.partner_name}
                                                </h5>
                                              </div>
                                              <span className="px-2 py-1 text-xs font-medium text-purple-700 bg-purple-100 rounded-full">
                                                {partner.branches?.length || 0}{" "}
                                                {partner.branches?.length === 1
                                                  ? "Branch"
                                                  : "Branches"}
                                              </span>
                                            </div>
                                          </div>

                                          {partner.branches &&
                                            partner.branches.length > 0 && (
                                              <div className="p-4">
                                                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                                  {partner.branches.map(
                                                    (branch, branchIndex) => (
                                                      <div
                                                        key={branchIndex}
                                                        className="p-3 bg-white rounded-lg border border-gray-100 transition-colors duration-200 hover:border-purple-200 hover:bg-purple-50/30"
                                                      >
                                                        <div className="flex gap-2 items-start mb-2">
                                                          <div className="w-2 h-2 bg-purple-500 rounded-full mt-1.5 flex-shrink-0" />
                                                          <div className="flex-1 min-w-0">
                                                            <h6 className="font-medium text-gray-900 truncate">
                                                              {branch.name}
                                                            </h6>
                                                          </div>
                                                        </div>

                                                        <div className="space-y-1">
                                                          <div className="flex gap-2 items-center text-sm text-gray-600">
                                                            <Split className="flex-shrink-0 w-4 h-4 text-purple-500" />
                                                            <span className="truncate">
                                                              {branch.region} -{" "}
                                                              {branch.location}
                                                            </span>
                                                          </div>

                                                          {branch.district && (
                                                            <div className="flex gap-2 items-center text-sm text-gray-600">
                                                              <svg
                                                                className="flex-shrink-0 w-4 h-4 text-purple-500"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                viewBox="0 0 24 24"
                                                              >
                                                                <path
                                                                  strokeLinecap="round"
                                                                  strokeLinejoin="round"
                                                                  strokeWidth={
                                                                    2
                                                                  }
                                                                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                                                                />
                                                                <path
                                                                  strokeLinecap="round"
                                                                  strokeLinejoin="round"
                                                                  strokeWidth={
                                                                    2
                                                                  }
                                                                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                                                                />
                                                              </svg>
                                                              <span className="truncate">
                                                                {
                                                                  branch.district
                                                                }
                                                              </span>
                                                            </div>
                                                          )}

                                                          {branch.suburb && (
                                                            <div className="flex gap-2 items-center text-sm text-gray-600">
                                                              <svg
                                                                className="flex-shrink-0 w-4 h-4 text-purple-500"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                viewBox="0 0 24 24"
                                                              >
                                                                <path
                                                                  strokeLinecap="round"
                                                                  strokeLinejoin="round"
                                                                  strokeWidth={
                                                                    2
                                                                  }
                                                                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                                                                />
                                                              </svg>
                                                              <span className="truncate">
                                                                {branch.suburb}
                                                              </span>
                                                            </div>
                                                          )}

                                                          {branch.landmark && (
                                                            <div className="flex gap-2 items-center text-sm text-gray-600">
                                                              <svg
                                                                className="flex-shrink-0 w-4 h-4 text-purple-500"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                viewBox="0 0 24 24"
                                                              >
                                                                <path
                                                                  strokeLinecap="round"
                                                                  strokeLinejoin="round"
                                                                  strokeWidth={
                                                                    2
                                                                  }
                                                                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                                                />
                                                              </svg>
                                                              <span className="truncate">
                                                                {
                                                                  branch.landmark
                                                                }
                                                              </span>
                                                            </div>
                                                          )}

                                                          {branch.phone_number && (
                                                            <div className="flex gap-2 items-center text-sm text-gray-600">
                                                              <Phone className="flex-shrink-0 w-4 h-4 text-purple-500" />
                                                              <a
                                                                href={`tel:${branch.phone_number}`}
                                                                className="truncate hover:text-purple-600"
                                                              >
                                                                {
                                                                  branch.phone_number
                                                                }
                                                              </a>
                                                            </div>
                                                          )}

                                                          {branch.gpsAddress && (
                                                            <div className="flex gap-2 items-center text-sm text-gray-600">
                                                              <svg
                                                                className="flex-shrink-0 w-4 h-4 text-purple-500"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                viewBox="0 0 24 24"
                                                              >
                                                                <path
                                                                  strokeLinecap="round"
                                                                  strokeLinejoin="round"
                                                                  strokeWidth={
                                                                    2
                                                                  }
                                                                  d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
                                                                />
                                                              </svg>
                                                              <span className="font-mono text-xs truncate">
                                                                {
                                                                  branch.gpsAddress
                                                                }
                                                              </span>
                                                            </div>
                                                          )}
                                                        </div>
                                                      </div>
                                                    )
                                                  )}
                                                </div>
                                              </div>
                                            )}
                                        </div>
                                      )
                                    )}
                                  </div>
                                );
                              }

                              // Handle region_location (original format)
                              if (
                                deal.region_location &&
                                Array.isArray(deal.region_location) &&
                                deal.region_location.length > 0
                              ) {
                                if (
                                  deal.region_location.length === 1 &&
                                  deal.region_location[0].location === "all" &&
                                  deal.region_location[0].region === "all"
                                ) {
                                  locationSections.push(
                                    <div
                                      key="region_location_all"
                                      className="space-y-3"
                                    >
                                      <div className="overflow-hidden relative bg-white rounded-xl border border-green-200 shadow-sm group hover:border-green-300">
                                        <div className="absolute inset-0 bg-gradient-to-r opacity-0 transition-opacity duration-300 from-green-500/5 to-green-500/5 group-hover:opacity-100" />
                                        <div className="p-4">
                                          <div className="flex justify-between items-center mb-3">
                                            <div className="flex gap-2 items-center">
                                              <div className="w-2 h-2 bg-green-500 rounded-full" />
                                              <h5 className="font-medium text-gray-900">
                                                All Target Locations
                                              </h5>
                                            </div>
                                            <span className="px-2 py-1 text-xs font-medium text-green-600 bg-green-100 rounded-full">
                                              Nationwide
                                            </span>
                                          </div>
                                          <div className="space-y-2">
                                            <div className="flex gap-2 items-center text-sm text-gray-600">
                                              <Split className="w-4 h-4 text-green-500" />
                                              <span>
                                                Available across all regions and
                                                cities
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                } else {
                                  locationSections.push(
                                    <div
                                      key="region_location"
                                      className="space-y-3"
                                    >
                                      <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200 shadow-sm">
                                        <div className="flex gap-2 items-center mb-3">
                                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                          <h5 className="font-semibold text-green-800">
                                            Regional Locations
                                          </h5>
                                          <span className="px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                                            {deal.region_location.length}{" "}
                                            {deal.region_location.length === 1
                                              ? "Location"
                                              : "Locations"}
                                          </span>
                                        </div>
                                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                          {deal.region_location.map(
                                            (locale, index) => (
                                              <div
                                                key={index}
                                                className="p-3 bg-white rounded-lg border border-green-100 transition-all duration-200 hover:border-green-200 hover:shadow-sm"
                                              >
                                                <div className="flex gap-3 items-start">
                                                  <div className="flex-shrink-0 mt-2 w-2 h-2 bg-green-500 rounded-full" />
                                                  <div className="flex-1 min-w-0">
                                                    <h6 className="mb-1 font-medium text-gray-900">
                                                      {locale.location}
                                                    </h6>
                                                    <div className="flex gap-2 items-center text-sm text-gray-600">
                                                      <Split className="flex-shrink-0 w-4 h-4 text-green-500" />
                                                      <span>
                                                        {locale.region}
                                                      </span>
                                                    </div>
                                                  </div>
                                                </div>
                                              </div>
                                            )
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                }
                              }

                              // Return all location sections or no data message
                              if (locationSections.length === 0) {
                                return (
                                  <div className="flex justify-center items-center p-6 bg-gray-50 rounded-xl">
                                    <div className="text-center">
                                      <div className="flex justify-center items-center mx-auto mb-3 w-12 h-12 bg-gray-200 rounded-full">
                                        <Split className="w-6 h-6 text-gray-400" />
                                      </div>
                                      <p className="text-gray-500">
                                        No target location information available
                                      </p>
                                    </div>
                                  </div>
                                );
                              }

                              return (
                                <div className="space-y-4">
                                  {locationSections}
                                </div>
                              );
                            })()}
                          </div>

                          {/* Redemption Locations Section */}
                          <div>
                            <div className="flex gap-3 items-center mb-4">
                              <div className="w-1 h-8 bg-gradient-to-b from-green-500 to-green-600 rounded-full" />
                              <h4 className="text-lg font-semibold text-gray-900">
                                Redemption Locations
                              </h4>
                            </div>

                            {(() => {
                              const redemptionSections = [];

                              // Handle redemption_branches (array of branch objects)
                              if (
                                deal.redemption_branches &&
                                Array.isArray(deal.redemption_branches) &&
                                deal.redemption_branches.length > 0
                              ) {
                                redemptionSections.push(
                                  <div
                                    key="redemption_branches"
                                    className="space-y-3"
                                  >
                                    <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200 shadow-sm">
                                      <div className="flex gap-2 items-center mb-3">
                                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                        <h5 className="font-semibold text-green-800">
                                          Redemption Branches
                                        </h5>
                                        <span className="px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                                          {deal.redemption_branches.length}{" "}
                                          {deal.redemption_branches.length === 1
                                            ? "Branch"
                                            : "Branches"}
                                        </span>
                                      </div>
                                      <div className="grid grid-cols-1 gap-3">
                                        {deal.redemption_branches.map(
                                          (branch, index) => (
                                            <div
                                              key={index}
                                              className="p-3 bg-white rounded-lg border border-green-100 transition-all duration-200 hover:border-green-200 hover:shadow-sm"
                                            >
                                              <div className="flex gap-3 items-start">
                                                <div className="flex-shrink-0 mt-2 w-2 h-2 bg-green-500 rounded-full" />
                                                <div className="flex-1 min-w-0">
                                                  <h6 className="mb-1 font-medium text-gray-900">
                                                    {branch.name ||
                                                      branch.branch_name}
                                                  </h6>
                                                  <div className="flex gap-2 items-center text-sm text-gray-600">
                                                    <Split className="flex-shrink-0 w-4 h-4 text-green-500" />
                                                    <span>
                                                      {branch.location},{" "}
                                                      {branch.region}
                                                    </span>
                                                  </div>
                                                  {branch.phone_number && (
                                                    <div className="flex gap-2 items-center mt-1 text-sm text-gray-600">
                                                      <Phone className="flex-shrink-0 w-4 h-4 text-green-500" />
                                                      <a
                                                        href={`tel:${branch.phone_number}`}
                                                        className="hover:text-green-600"
                                                      >
                                                        {branch.phone_number}
                                                      </a>
                                                    </div>
                                                  )}
                                                  {branch.google_url && (
                                                    <div className="flex gap-2 items-center mt-1 text-sm text-gray-600">
                                                      <svg
                                                        className="flex-shrink-0 w-4 h-4 text-green-500"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                      >
                                                        <path
                                                          strokeLinecap="round"
                                                          strokeLinejoin="round"
                                                          strokeWidth={2}
                                                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                                        />
                                                      </svg>
                                                      <a
                                                        href={branch.google_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="hover:text-green-600"
                                                      >
                                                        View on Google Maps
                                                      </a>
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          )
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              }

                              // Handle redemption_partners (array of partner objects with branches)
                              if (
                                deal.redemption_partners &&
                                Array.isArray(deal.redemption_partners) &&
                                deal.redemption_partners.length > 0
                              ) {
                                redemptionSections.push(
                                  <div
                                    key="redemption_partners"
                                    className="space-y-4"
                                  >
                                    {deal.redemption_partners.map(
                                      (partner, partnerIndex) => (
                                        <div
                                          key={partnerIndex}
                                          className="overflow-hidden bg-white rounded-xl border border-green-100 shadow-sm"
                                        >
                                          <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-100">
                                            <div className="flex justify-between items-center">
                                              <div className="flex gap-2 items-center">
                                                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                                <h5 className="font-semibold text-green-800">
                                                  {partner.partner_name}
                                                </h5>
                                              </div>
                                              <span className="px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                                                {partner.branches?.length || 0}{" "}
                                                {partner.branches?.length === 1
                                                  ? "Branch"
                                                  : "Branches"}
                                              </span>
                                            </div>
                                          </div>

                                          {partner.branches &&
                                            partner.branches.length > 0 && (
                                              <div className="p-4">
                                                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                                  {partner.branches.map(
                                                    (branch, branchIndex) => (
                                                      <div
                                                        key={branchIndex}
                                                        className="p-3 bg-white rounded-lg border border-gray-100 transition-colors duration-200 hover:border-green-200 hover:bg-green-50/30"
                                                      >
                                                        <div className="flex gap-2 items-start mb-2">
                                                          <div className="w-2 h-2 bg-green-500 rounded-full mt-1.5 flex-shrink-0" />
                                                          <div className="flex-1 min-w-0">
                                                            <h6 className="font-medium text-gray-900 truncate">
                                                              {branch.name}
                                                            </h6>
                                                          </div>
                                                        </div>

                                                        <div className="space-y-1">
                                                          <div className="flex gap-2 items-center text-sm text-gray-600">
                                                            <Split className="flex-shrink-0 w-4 h-4 text-green-500" />
                                                            <span className="truncate">
                                                              {branch.region} -{" "}
                                                              {branch.location}
                                                            </span>
                                                          </div>

                                                          {branch.phone_number && (
                                                            <div className="flex gap-2 items-center text-sm text-gray-600">
                                                              <Phone className="flex-shrink-0 w-4 h-4 text-green-500" />
                                                              <a
                                                                href={`tel:${branch.phone_number}`}
                                                                className="truncate hover:text-green-600"
                                                              >
                                                                {
                                                                  branch.phone_number
                                                                }
                                                              </a>
                                                            </div>
                                                          )}

                                                          {branch.google_url && (
                                                            <div className="flex gap-2 items-center text-sm text-gray-600">
                                                              <svg
                                                                className="flex-shrink-0 w-4 h-4 text-green-500"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                viewBox="0 0 24 24"
                                                              >
                                                                <path
                                                                  strokeLinecap="round"
                                                                  strokeLinejoin="round"
                                                                  strokeWidth={
                                                                    2
                                                                  }
                                                                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                                                />
                                                              </svg>
                                                              <a
                                                                href={
                                                                  branch.google_url
                                                                }
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="truncate hover:text-green-600"
                                                              >
                                                                View on Google
                                                                Maps
                                                              </a>
                                                            </div>
                                                          )}
                                                        </div>
                                                      </div>
                                                    )
                                                  )}
                                                </div>
                                              </div>
                                            )}
                                        </div>
                                      )
                                    )}
                                  </div>
                                );
                              }

                              // Handle redemption_region_location (array of region/location objects)
                              if (
                                deal.redemption_region_location &&
                                Array.isArray(
                                  deal.redemption_region_location
                                ) &&
                                deal.redemption_region_location.length > 0
                              ) {
                                if (
                                  deal.redemption_region_location.length ===
                                    1 &&
                                  deal.redemption_region_location[0]
                                    .location === "all" &&
                                  deal.redemption_region_location[0].region ===
                                    "all"
                                ) {
                                  redemptionSections.push(
                                    <div
                                      key="redemption_region_location_all"
                                      className="space-y-3"
                                    >
                                      <div className="overflow-hidden relative bg-white rounded-xl border border-green-200 shadow-sm group hover:border-green-300">
                                        <div className="absolute inset-0 bg-gradient-to-r opacity-0 transition-opacity duration-300 from-green-500/5 to-green-500/5 group-hover:opacity-100" />
                                        <div className="p-4">
                                          <div className="flex justify-between items-center mb-3">
                                            <div className="flex gap-2 items-center">
                                              <div className="w-2 h-2 bg-green-500 rounded-full" />
                                              <h5 className="font-medium text-gray-900">
                                                All Redemption Locations
                                              </h5>
                                            </div>
                                            <span className="px-2 py-1 text-xs font-medium text-green-600 bg-green-100 rounded-full">
                                              Nationwide
                                            </span>
                                          </div>
                                          <div className="space-y-2">
                                            <div className="flex gap-2 items-center text-sm text-gray-600">
                                              <Split className="w-4 h-4 text-green-500" />
                                              <span>
                                                Available across all regions and
                                                cities
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                } else {
                                  redemptionSections.push(
                                    <div
                                      key="redemption_region_location"
                                      className="space-y-3"
                                    >
                                      <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200 shadow-sm">
                                        <div className="flex gap-2 items-center mb-3">
                                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                          <h5 className="font-semibold text-green-800">
                                            Regional Redemption Locations
                                          </h5>
                                          <span className="px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                                            {
                                              deal.redemption_region_location
                                                .length
                                            }{" "}
                                            {deal.redemption_region_location
                                              .length === 1
                                              ? "Location"
                                              : "Locations"}
                                          </span>
                                        </div>
                                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                          {deal.redemption_region_location.map(
                                            (locale, index) => (
                                              <div
                                                key={index}
                                                className="p-3 bg-white rounded-lg border border-green-100 transition-all duration-200 hover:border-green-200 hover:shadow-sm"
                                              >
                                                <div className="flex gap-3 items-start">
                                                  <div className="flex-shrink-0 mt-2 w-2 h-2 bg-green-500 rounded-full" />
                                                  <div className="flex-1 min-w-0">
                                                    <h6 className="mb-1 font-medium text-gray-900">
                                                      {locale.location}
                                                    </h6>
                                                    <div className="flex gap-2 items-center text-sm text-gray-600">
                                                      <Split className="flex-shrink-0 w-4 h-4 text-green-500" />
                                                      <span>
                                                        {locale.region}
                                                      </span>
                                                    </div>
                                                  </div>
                                                </div>
                                              </div>
                                            )
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                }
                              }

                              // Return all redemption sections or no data message
                              if (redemptionSections.length === 0) {
                                return (
                                  <div className="flex justify-center items-center p-6 bg-gray-50 rounded-xl">
                                    <div className="text-center">
                                      <div className="flex justify-center items-center mx-auto mb-3 w-12 h-12 bg-gray-200 rounded-full">
                                        <Split className="w-6 h-6 text-gray-400" />
                                      </div>
                                      <p className="text-gray-500">
                                        No redemption location information
                                        available
                                      </p>
                                    </div>
                                  </div>
                                );
                              }

                              return (
                                <div className="space-y-4">
                                  {redemptionSections}
                                </div>
                              );
                            })()}
                          </div>
                        </motion.div>
                      )}

                      {activeTab === "redemption" && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="space-y-4"
                        >
                          {/* Redemption Type */}
                          {(deal.redemption_type || deal.redemptionType) &&
                            (deal.redemption_type || deal.redemptionType)
                              .length > 0 && (
                              <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                                <h4 className="mb-2 text-sm font-medium text-blue-700">
                                  Redemption Type
                                </h4>
                                <div className="flex flex-wrap gap-2">
                                  {(
                                    deal.redemption_type || deal.redemptionType
                                  ).map((type, index) => (
                                    <span
                                      key={index}
                                      className="px-3 py-1 text-sm text-blue-700 bg-blue-100 rounded-full"
                                    >
                                      {type}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                          {/* Redemption Instructions */}
                          {(deal?.details?.redemption_instructions ||
                            deal?.details?.redemptionInstructions) && (
                            <div className="p-3 bg-purple-50 rounded-lg border border-purple-100">
                              <h4 className="mb-2 text-sm font-medium text-purple-700">
                                Redemption Instructions
                              </h4>
                              <p className="text-sm text-gray-600">
                                {deal?.details?.redemption_instructions ||
                                  deal?.details?.redemptionInstructions}
                              </p>
                            </div>
                          )}

                          {/* Redemption Partners */}
                          {deal.redemption_partners &&
                            deal.redemption_partners.length > 0 && (
                              <div className="p-3 bg-green-50 rounded-lg border border-green-100">
                                <h4 className="mb-2 text-sm font-medium text-green-700">
                                  Redemption Partners
                                </h4>
                                <div className="space-y-3">
                                  {deal.redemption_partners.map(
                                    (partner, partnerIndex) => (
                                      <div
                                        key={partnerIndex}
                                        className="p-3 bg-white rounded border"
                                      >
                                        <h5 className="mb-2 font-medium text-gray-900">
                                          {partner.partner_name}
                                        </h5>
                                        <div className="grid grid-cols-1 gap-2">
                                          {partner.branches.map(
                                            (branch, branchIndex) => (
                                              <div
                                                key={branchIndex}
                                                className="flex justify-between items-center text-sm"
                                              >
                                                <span className="text-gray-600">
                                                  {branch.name}
                                                </span>
                                                <span className="text-gray-500">
                                                  {branch.location},{" "}
                                                  {branch.region}
                                                </span>
                                              </div>
                                            )
                                          )}
                                        </div>
                                      </div>
                                    )
                                  )}
                                </div>
                              </div>
                            )}

                          {/* Redemption Limits */}
                          {deal.redemption_limits &&
                            deal.redemption_limits.length > 0 && (
                              <div className="p-3 bg-orange-50 rounded-lg border border-orange-100">
                                <h4 className="mb-2 text-sm font-medium text-orange-700">
                                  Redemption Limits
                                </h4>
                                <ul className="space-y-1">
                                  {deal.redemption_limits.map(
                                    (limit, index) => (
                                      <li
                                        key={index}
                                        className="flex gap-2 items-start text-sm text-orange-600"
                                      >
                                        <span className="text-orange-500 mt-0.5">
                                          •
                                        </span>
                                        <span>{limit}</span>
                                      </li>
                                    )
                                  )}
                                </ul>
                              </div>
                            )}

                          {/* Age Restrictions */}
                          {deal.age && deal.age.length > 0 && (
                            <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-100">
                              <h4 className="mb-2 text-sm font-medium text-yellow-700">
                                Age Restrictions
                              </h4>
                              {deal.age.map((ageItem, index) => (
                                <div
                                  key={index}
                                  className="text-sm text-yellow-600"
                                >
                                  {ageItem.no_age_limit ? (
                                    <span>No age restrictions</span>
                                  ) : (
                                    <span>
                                      {ageItem.min_age && ageItem.max_age
                                        ? `Ages ${ageItem.min_age} - ${ageItem.max_age}`
                                        : ageItem.min_age
                                        ? `Minimum age: ${ageItem.min_age}`
                                        : ageItem.max_age
                                        ? `Maximum age: ${ageItem.max_age}`
                                        : "Age restrictions apply"}
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Gender Restrictions */}
                          {deal.gender && deal.gender !== "All Genders" && (
                            <div className="p-3 bg-pink-50 rounded-lg border border-pink-100">
                              <h4 className="mb-2 text-sm font-medium text-pink-700">
                                Gender Restrictions
                              </h4>
                              <span className="text-sm text-pink-600">
                                {deal.gender}
                              </span>
                            </div>
                          )}

                          {/* Delivery Information */}
                          {deal.delivery_fee && (
                            <div className="p-4 bg-gradient-to-br from-blue-50 via-cyan-50 to-teal-50 rounded-xl border border-blue-200 shadow-sm">
                              <div className="flex gap-2 items-center mb-4">
                                <svg
                                  className="w-5 h-5 text-blue-600"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                                  />
                                </svg>
                                <h4 className="text-sm font-semibold text-blue-800">
                                  Delivery Information
                                </h4>
                              </div>

                              <div className="space-y-3">
                                {/* Delivery Fee Status */}
                                <div className="p-3 rounded-lg border border-blue-100 backdrop-blur-sm bg-white/70">
                                  <div className="flex gap-2 items-center mb-2">
                                    <div
                                      className={`w-2 h-2 rounded-full ${
                                        deal.delivery_fee === "apply"
                                          ? "bg-orange-500"
                                          : "bg-green-500"
                                      }`}
                                    ></div>
                                    <span className="text-xs font-medium text-blue-700">
                                      Delivery Fee
                                    </span>
                                  </div>
                                  <div className="flex gap-2 items-center">
                                    <span
                                      className={`text-sm font-semibold ${
                                        deal.delivery_fee === "apply"
                                          ? "text-orange-700"
                                          : "text-green-700"
                                      }`}
                                    >
                                      {deal.delivery_fee === "apply"
                                        ? "Delivery Fee Applies"
                                        : "Free Delivery"}
                                    </span>
                                    {deal.delivery_fee === "apply" && (
                                      <span className="px-2 py-1 text-xs font-medium text-orange-700 bg-orange-100 rounded-full">
                                        Paid
                                      </span>
                                    )}
                                    {deal.delivery_fee !== "apply" && (
                                      <span className="px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                                        Free
                                      </span>
                                    )}
                                  </div>
                                </div>

                                {/* Free Delivery Areas */}
                                {deal.free_delivery_areas &&
                                  Array.isArray(deal.free_delivery_areas) &&
                                  deal.free_delivery_areas.length > 0 && (
                                    <div className="p-3 rounded-lg border border-teal-100 backdrop-blur-sm bg-white/70">
                                      <div className="flex gap-2 items-center mb-3">
                                        <svg
                                          className="w-4 h-4 text-teal-600"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                                          />
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                                          />
                                        </svg>
                                        <span className="text-xs font-medium text-teal-700">
                                          Free Delivery Areas
                                        </span>
                                        <span className="px-2 py-1 text-xs font-medium text-teal-700 bg-teal-100 rounded-full">
                                          {deal.free_delivery_areas.length}{" "}
                                          {deal.free_delivery_areas.length === 1
                                            ? "Area"
                                            : "Areas"}
                                        </span>
                                      </div>

                                      <div className="space-y-2">
                                        {deal.free_delivery_areas.map(
                                          (area, index) => (
                                            <div
                                              key={index}
                                              className="p-2 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-lg border border-teal-100"
                                            >
                                              <div className="flex gap-2 items-start">
                                                <div className="flex-shrink-0 mt-2 w-2 h-2 bg-teal-500 rounded-full"></div>
                                                <div className="flex-1 min-w-0">
                                                  <div className="mb-1 text-sm font-medium text-gray-900">
                                                    {area.region}
                                                  </div>
                                                  <div className="text-xs text-gray-600">
                                                    {area.location}
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          )
                                        )}
                                      </div>

                                      <div className="p-2 mt-3 bg-gradient-to-r from-teal-100 to-cyan-100 rounded-md">
                                        <span className="text-xs font-medium text-teal-800">
                                          Free delivery available in{" "}
                                          {deal.free_delivery_areas.length}{" "}
                                          {deal.free_delivery_areas.length === 1
                                            ? "region"
                                            : "regions"}
                                        </span>
                                      </div>
                                    </div>
                                  )}
                              </div>
                            </div>
                          )}
                        </motion.div>
                      )}

                      {activeTab === "terms" && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="space-y-6"
                        >
                          {/* Redemption Instructions */}
                          {(deal?.details?.redemption_instructions ||
                            deal?.details?.redemptionInstructions) && (
                            <motion.div
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: 0.1 }}
                              className="overflow-hidden relative p-6 bg-gradient-to-br from-purple-50 to-white rounded-2xl border border-purple-100 group"
                            >
                              <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-purple-50 rounded-full opacity-50 filter blur-2xl transition-opacity duration-500 group-hover:opacity-75" />
                              <div className="relative">
                                <div className="flex gap-3 items-center mb-4">
                                  <div className="p-2 bg-purple-100 rounded-lg">
                                    <Ticket className="w-5 h-5 text-purple-600" />
                                  </div>
                                  <h3 className="text-lg font-semibold text-gray-900">
                                    Redemption Instructions
                                  </h3>
                                </div>
                                <p className="leading-relaxed text-gray-600">
                                  {deal?.details?.redemption_instructions ||
                                    deal?.details?.redemptionInstructions}
                                </p>
                              </div>
                            </motion.div>
                          )}

                          {/* Refund Policy */}
                          {(deal?.refund_policy || deal?.refundPolicy) && (
                            <motion.div
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: 0.2 }}
                              className="overflow-hidden relative p-6 bg-gradient-to-br from-pink-50 to-white rounded-2xl border border-pink-100 group"
                            >
                              <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-pink-50 rounded-full opacity-50 filter blur-2xl transition-opacity duration-500 group-hover:opacity-75" />
                              <div className="relative">
                                <div className="flex gap-3 items-center mb-4">
                                  <div className="p-2 bg-pink-100 rounded-lg">
                                    <RefreshCw className="w-5 h-5 text-pink-600" />
                                  </div>
                                  <h3 className="text-lg font-semibold text-gray-900">
                                    Refund Policy
                                  </h3>
                                </div>
                                <p className="leading-relaxed text-gray-600">
                                  {deal?.refund_policy ||
                                    deal?.refundPolicy ||
                                    "All sales are final. No refunds or exchanges unless required by law."}
                                </p>
                              </div>
                            </motion.div>
                          )}
                          {/* Terms & Conditions */}
                          <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.3 }}
                            className="overflow-hidden relative p-6 bg-gradient-to-br from-blue-50 to-white rounded-2xl border border-blue-100 group"
                          >
                            <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-blue-50 rounded-full opacity-50 filter blur-2xl transition-opacity duration-500 group-hover:opacity-75" />
                            <div className="relative">
                              <div className="flex gap-3 items-center mb-4">
                                <div className="p-2 bg-blue-100 rounded-lg">
                                  <ScrollText className="w-5 h-5 text-blue-600" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900">
                                  Terms & Conditions
                                </h3>
                              </div>
                              <div className="space-y-3">
                                {(() => {
                                  const terms = deal?.terms;

                                  // Check if it's a URL
                                  if (
                                    terms &&
                                    (terms.startsWith("http://") ||
                                      terms.startsWith("https://"))
                                  ) {
                                    return (
                                      <div className="p-4 text-center bg-blue-50 rounded-lg border border-blue-200">
                                        <div className="mb-3">
                                          <svg
                                            className="mx-auto mb-2 w-12 h-12 text-blue-600"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth={2}
                                              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                            />
                                          </svg>
                                          <p className="text-sm font-medium text-blue-700">
                                            Terms available online
                                          </p>
                                        </div>
                                        <a
                                          href={terms}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="inline-flex gap-2 items-center px-4 py-2 text-white bg-blue-600 rounded-lg transition-colors duration-200 hover:bg-blue-700"
                                        >
                                          <svg
                                            className="w-4 h-4"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth={2}
                                              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                            />
                                          </svg>
                                          View Terms Online
                                        </a>
                                      </div>
                                    );
                                  }

                                  // Check if it's structured text (contains sections with colons OR bullet points OR numbered lists)
                                  if (
                                    terms &&
                                    (terms.includes(":") ||
                                      terms.includes("•") ||
                                      terms.includes("-") ||
                                      terms.includes("1.") ||
                                      terms.includes("TERMS"))
                                  ) {
                                    return (
                                      <div className="max-w-none prose prose-sm">
                                        <div className="font-sans text-sm leading-relaxed text-gray-600 whitespace-pre-wrap">
                                          {terms
                                            .split("\n")
                                            .map((line, index) => {
                                              // Handle section headers (lines ending with colon or containing TERMS)
                                              if (
                                                (line.trim().endsWith(":") &&
                                                  line.trim().length > 0) ||
                                                (line
                                                  .trim()
                                                  .toUpperCase()
                                                  .includes("TERMS") &&
                                                  line.trim().length > 0)
                                              ) {
                                                return (
                                                  <div
                                                    key={index}
                                                    className="mt-4 mb-2"
                                                  >
                                                    <h4 className="pb-1 text-base font-semibold text-gray-800 border-b border-gray-200">
                                                      {line.trim()}
                                                    </h4>
                                                  </div>
                                                );
                                              }

                                              // Handle bullet points
                                              if (
                                                line.trim().startsWith("•") ||
                                                line.trim().startsWith("-")
                                              ) {
                                                return (
                                                  <div
                                                    key={index}
                                                    className="mb-1 ml-4"
                                                  >
                                                    <span className="text-gray-600">
                                                      {line.trim()}
                                                    </span>
                                                  </div>
                                                );
                                              }

                                              // Handle numbered lists
                                              if (line.trim().match(/^\d+\./)) {
                                                return (
                                                  <div
                                                    key={index}
                                                    className="mb-1 ml-4"
                                                  >
                                                    <span className="text-gray-600">
                                                      {line.trim()}
                                                    </span>
                                                  </div>
                                                );
                                              }

                                              // Handle empty lines
                                              if (line.trim() === "") {
                                                return (
                                                  <div
                                                    key={index}
                                                    className="h-2"
                                                  ></div>
                                                );
                                              }

                                              // Handle regular text
                                              return (
                                                <div
                                                  key={index}
                                                  className="mb-1"
                                                >
                                                  <span className="text-gray-600">
                                                    {line}
                                                  </span>
                                                </div>
                                              );
                                            })}
                                        </div>
                                      </div>
                                    );
                                  }

                                  // Regular text or fallback
                                  return (
                                    <div className="max-w-none prose prose-sm">
                                      <pre className="font-sans text-sm leading-relaxed text-gray-600 whitespace-pre-wrap">
                                        {terms ||
                                          "By using this offer, you agree to the following terms and conditions. Management reserves the right to modify or cancel this offer at any time."}
                                      </pre>
                                    </div>
                                  );
                                })()}

                                {/* Additional Info Notice */}
                                <motion.div
                                  initial={{ opacity: 0, y: 10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{ delay: 0.4 }}
                                  className="flex gap-2 items-center p-3 mt-4 rounded-lg border border-blue-100 bg-blue-50/50"
                                >
                                  <AlertCircle className="w-4 h-4 text-blue-600" />
                                  <span className="text-sm text-blue-600">
                                    These terms are subject to change. Last
                                    updated {moment().format("MMMM DD, YYYY")}
                                  </span>
                                </motion.div>
                              </div>
                            </div>
                          </motion.div>
                          {/* Footer Notice */}
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.5 }}
                            className="flex gap-2 justify-center items-center mt-6 text-sm text-gray-500"
                          >
                            <Info className="w-4 h-4" />
                            <span>
                              Please read all terms carefully before redeeming
                              the offer
                            </span>
                          </motion.div>
                        </motion.div>
                      )}
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex flex-shrink-0 gap-3 justify-end items-center p-4 bg-gray-50 rounded-b-2xl border-t md:gap-4 md:p-6">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={onCloseViewModal}
                className="px-4 py-2 text-gray-600 rounded-xl transition-colors duration-200 hover:bg-gray-100"
              >
                Close
              </motion.button>
              {canEditOffers && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    onCloseViewModal();
                    handleEditNavigation(deal);
                  }}
                  className="px-4 py-2 bg-gradient-to-r from-[#FF6C6C] to-pink-600 
                       text-white rounded-xl shadow-lg shadow-pink-500/30
                       hover:shadow-xl hover:shadow-pink-500/40
                       transition-all duration-200"
                >
                  Edit Deal
                </motion.button>
              )}
            </div>
          </motion.div>
          </div>

          {/* Full-Screen Image Gallery Modal */}
          <AnimatePresence>
            {showImageGallery && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 bg-black/90 z-[2000] backdrop-blur-sm"
                  onClick={() => setShowImageGallery(false)}
                />
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className="fixed inset-0 z-[2001] flex items-center justify-center p-4"
                >
                  <div className="relative max-w-4xl max-h-[90vh] w-full">
                    {/* Close Button */}
                    <button
                      onClick={() => setShowImageGallery(false)}
                      className="absolute top-4 right-4 z-10 p-2 text-white rounded-full transition-colors bg-black/50 hover:bg-black/70"
                    >
                      <X className="w-6 h-6" />
                    </button>

                    {/* Main Image */}
                    <div className="overflow-hidden relative bg-white rounded-lg">
                      <img
                        src={getAllImages()[currentImageIndex]}
                        alt={`Gallery ${currentImageIndex + 1}`}
                        className="w-full h-auto max-h-[80vh] object-contain"
                      />
                    </div>

                    {/* Navigation Arrows */}
                    {getAllImages().length > 1 && (
                      <>
                        <button
                          onClick={prevImage}
                          className="absolute left-4 top-1/2 p-2 text-white rounded-full transition-colors -translate-y-1/2 bg-black/50 hover:bg-black/70"
                        >
                          <ChevronLeft className="w-6 h-6" />
                        </button>
                        <button
                          onClick={nextImage}
                          className="absolute right-4 top-1/2 p-2 text-white rounded-full transition-colors -translate-y-1/2 bg-black/50 hover:bg-black/70"
                        >
                          <ChevronRight className="w-6 h-6" />
                        </button>
                      </>
                    )}

                    {/* Image Counter */}
                    {getAllImages().length > 1 && (
                      <div className="absolute bottom-4 left-1/2 px-3 py-1 text-sm text-white rounded-full -translate-x-1/2 bg-black/50">
                        {currentImageIndex + 1} / {getAllImages().length}
                      </div>
                    )}

                    {/* Thumbnail Strip */}
                    {getAllImages().length > 1 && (
                      <div className="flex overflow-x-auto absolute bottom-16 left-1/2 gap-2 max-w-full -translate-x-1/2">
                        {getAllImages().map((image, index) => (
                          <img
                            key={index}
                            src={image}
                            alt={`Thumbnail ${index + 1}`}
                            className={`w-16 h-16 rounded-lg cursor-pointer object-cover flex-shrink-0 border-2 transition-all ${
                              index === currentImageIndex
                                ? "border-white"
                                : "border-transparent hover:border-white/50"
                            }`}
                            onClick={() => setCurrentImageIndex(index)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </>
      )}
      <PropertiesModal
        open={isPropertiesModalOpen}
        onClose={() => setIsPropertiesModalOpen(false)}
        properties={deal?.properties || []}
        dealTitle={getVendorDealLabel(deal)}
      />
    </AnimatePresence>
  );
};

export default ViewOffer;
